import { cloneDeep, isBlank } from '@shared/object'
import { getCurrentPage, isInitialVisitPending, router } from '@shared/inertia'
import { usePage } from '@inertiajs/vue3'

const corporatePublic = new Set([
  '/corp/forgot_password',
  '/corp/lets_talk',
  '/corp/login',
  '/corp/privacy_policy',
  '/corp/reset_password',
  '/corp/sample',
  '/corp/signup',
  '/corp/terms_and_conditions',
  '/corp/verify',
])

export function appFor (path: string) {
  path = new URL(path, location.origin).pathname

  if (path.startsWith('/admin/'))
    return 'admin'

  if (path.startsWith('/claim') || path.startsWith('/corp/redeem/'))
    return 'redemption'

  if (corporatePublic.has(path))
    return 'corporate_public'

  if (path.startsWith('/corp/'))
    return 'corporate'

  if (path.startsWith('/internal/'))
    return 'internal'

  if (
    path.startsWith('/about')
    || path.startsWith('/buy-a-gift')
    || path.startsWith('/gift-design')
    || path.startsWith('/how-it-works')
    || path.startsWith('/giftagreement')
    || path.startsWith('/cardholderagreement')
    || path.startsWith('/gift-card-checkout')
    || path.startsWith('/legal')
    || path.startsWith('/terms')
    || path.startsWith('/login')
    || path.startsWith('/lp')
    || path.startsWith('/my-account')
    || path.startsWith('/nlp')
    || path.startsWith('/press')
    || path.startsWith('/privacy')
    || path.startsWith('/signup')
    || path.startsWith('/support')
    || path.startsWith('/team')
    || path.startsWith('/gift/landing')
    || path.startsWith('/checkout')
    || path.startsWith('/order/confirmation/')
  )
    return 'consumer'
}

// Public: Checks if the given path is the same Vue app
export function isSameApp (url: string) {
  try {
    const inertiaProps = usePage()?.props || (!import.meta.env.SSR && (window as any).InertiaProps)
    const currentApp = inertiaProps?.layout || appFor(location.pathname)
    return appFor(url) === currentApp
  }
  catch {
    return false
  }
}

// Public: The query parameters of the current URL.
export function currentParams () {
  if (import.meta.env.SSR) return new URLSearchParams()

  return new URLSearchParams(location.search)
}

interface NavigationOptions {
  replace?: boolean
  pathname?: string
  component?: string
}

// Public: Allows to push a new URL with new query parameters.
export function pushQuery (queryParams: Record<string, any>, options?: NavigationOptions) {
  const params = currentParams()
  const oldParams = params.toString()
  Object.entries(queryParams).forEach(([name, value]) => {
    isBlank(value) ? params.delete(name) : params.set(name, value)
  })
  const newParams = params.toString()

  if (newParams !== oldParams) {
    const expectedPathname = options?.pathname || location.pathname
    const newUrl = newParams ? `${expectedPathname}?${newParams}` : expectedPathname

    // Update the browser URL synchronously via `window.history` instead of
    // `router.push`/`router.replace`. The router methods (a) fire
    // `inertia:navigate`, which `ModalsManager` listens to and uses to
    // close all open modals — so a modal that calls `pushQuery` from its
    // own `onMounted` would flicker shut, and (b) are async (queued
    // through Inertia's `clientVisitQueue`), so a follow-up `pushQuery`
    // reads a stale `location.pathname` and ends up writing back the
    // *previous* page's path. We preserve Inertia's history-state shape
    // (`{...state, page: {..., url}}`) so the browser back/forward →
    // `popstate` handler still restores correctly. `replace` vs `push`
    // matters because some flows (e.g. GiftWizardModal.close) call
    // `history.back()` and expect the previous page entry to be there.
    const oldState = window.history.state
    const newState = oldState?.page
      ? { ...oldState, page: { ...oldState.page, url: newUrl } }
      : oldState
    window.history[options?.replace ? 'replaceState' : 'pushState'](newState, '', newUrl)

    // Inertia v3 enqueues async history operations during page mount (via
    // `Promise.resolve().then` chains) using its internally-tracked page URL
    // (the URL the server returned, without our query string). Our synchronous
    // write above lands *before* that queue drains, so the queued write would
    // clobber our URL when it flushes. While it may be pending, also route the
    // new URL through Inertia's client-visit queue: it serializes after the
    // pending write, so ours deterministically wins. `preserveState` skips the
    // component remount, and since `location` already matches the URL,
    // `page.set` forces a history *replace* and does not fire
    // `inertia:navigate` — no extra history entry, and modals stay open.
    //
    // ONLY during that initial window: once the mount write has flushed there
    // is nothing left to outlast, and the visit — which writes the URL it
    // captured at enqueue time — becomes a hazard instead: it can re-apply a
    // stale URL over a navigation that lands in between, e.g. the
    // `history.back()` that closes GiftWizardModal/CustomizerModal.
    if (getCurrentPage() && isInitialVisitPending()) {
      router.replace({
        url: newUrl,
        preserveState: true,
        preserveScroll: true,
      })
    }
  }
}

// Public: Allows to add/update/remove a query parameter in the current URL.
export function replaceQuery (queryParams: Record<string, any>) {
  return pushQuery(queryParams, { replace: true })
}

// Public: Allows to update state in the current page that should persist
// across navigations. Goes through `router.replace` (with `preserveState`)
// so Vue's reactive `usePage()` sees the new props — components watching
// `page.props.X` (e.g. the corporate search composable's `useSearchData`)
// rely on this. `preserveState: true` skips the remount so we don't re-run
// `onMounted` hooks. Inertia stores the resulting page in browser history
// state automatically, so back/forward restore replays the saved attrs.
//
// Passing `url: location.href` is critical: without it, `router.replace`
// uses Inertia's tracked page URL (the URL at the last full Inertia visit),
// which clobbers any query-string updates we wrote synchronously via
// `pushQuery`/`history.replaceState`. The corporate search flow updates the
// URL with filter/sort params via `replaceQuery` BEFORE calling
// `saveInertiaState`, so the search filters end up in subsequent request
// `Referer` headers — which `redirect_back` (e.g. after deleting a row)
// depends on to return to the filtered list.
export function saveInertiaState (attrs: any) {
  if (!getCurrentPage()) return

  router.replace({
    url: location.href,
    props: props => ({ ...props, ...cloneDeep(attrs) }),
    preserveState: true,
    preserveScroll: true,
  })
}

export function makeAbsoluteUrl (path: string) {
  return new URL(path, document.baseURI).href
}
